import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, HelpCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FAQSection — embeddable inline block for the home page. Shows the first
 * `limit` FAQ items and links to the full /faq page for the rest.
 */
export default function FAQSection({ limit = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/public/faq`)
      .then(r => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const shown = items.slice(0, limit);
  const hasMore = items.length > limit;

  return (
    <section data-testid="home-faq-section" className="mx-auto max-w-4xl px-5 sm:px-8 py-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3 text-electric text-xs tracking-[0.4em]">
          <HelpCircle size={14} /> FREQUENTLY ASKED
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl mb-3">Answers before you book</h2>
        <p className="text-white/60 text-sm max-w-xl mx-auto">
          The essentials on our chauffeur service, rentals, insurance, and policies.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center text-white/50 text-sm py-8">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : shown.length === 0 ? null : (
        <div className="space-y-2">
          {shown.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={item.id}
                data-testid={`home-faq-item-${i}`}
                className={`rounded-2xl border transition ${
                  open ? "border-electric/50 bg-electric/[0.04]" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-start justify-between gap-4 text-left px-5 py-4"
                >
                  <span className={`font-serif text-lg leading-snug ${open ? "text-electric" : "text-white"}`}>
                    {item.question}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`shrink-0 mt-1 text-white/50 transition-transform ${open ? "rotate-180 text-electric" : ""}`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-5 pt-1 text-white/70 text-sm leading-relaxed whitespace-pre-wrap">
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="mt-8 text-center">
          <Link
            to="/faq"
            data-testid="home-faq-viewall"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-electric/40 text-electric hover:bg-electric/10 transition text-sm label-caps"
          >
            View all {items.length} questions →
          </Link>
        </div>
      )}
    </section>
  );
}
